import { Calendar, CalendarDays, Bell, Users, MessageCircle, Trophy, Settings, LogOut, Shield, BarChart } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/hooks/use-sidebar";
import { useMessage } from "@/hooks/use-message";
import { LanguageToggle } from "@/components/ui/language-toggle";
import { UnreadMessageBadge } from "@/components/layout/UnreadMessageBadge";
import { Suspense } from "react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { withAuthRoutePrefix } from "@/config/auth";

export function AppSidebar() {
  const { state, setOpenMobile } = useSidebar();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { setActiveConversation } = useMessage();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const currentPath = location.pathname;

  const items = [
    { titleKey: "nav.events", url: "/", icon: Calendar },
    { titleKey: "nav.calendar", url: "/calendar", icon: CalendarDays },
    { titleKey: "nav.notifications", url: "/notifications", icon: Bell },
    { titleKey: "nav.messages", url: "/messages", icon: MessageCircle },
    { titleKey: "nav.profile", url: "/profile", icon: Settings }
  ];

  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? "bg-accent text-accent-foreground font-medium" : "hover:bg-accent/50";

  const allItems = user?.isAdmin ? [...items, { titleKey: "nav.admin", url: "/admin", icon: Shield }] : items;
  const isCollapsed = state === "collapsed";

  const handleNavClick = (url: string) => {
    if (url === '/messages') {
      setActiveConversation(null);
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  };
  return (
    <Sidebar
      collapsible="icon"
      className="bg-sidebar border-sidebar-border"
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            {!isCollapsed && `${t("welcome.back")}, ${user?.firstName}`}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {allItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={withAuthRoutePrefix(item.url, currentPath)} 
                      end 
                      className={getNavCls} 
                      onClick={() => handleNavClick(item.url)}
                      state={item.url === '/groups' ? { viewType: 'main' } : undefined}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <item.icon className="h-4 w-4 shrink-0" />
                        {!isCollapsed && <span className="flex-1 min-w-0">{t(item.titleKey)}</span>}
                        {!isCollapsed && item.url === '/messages' && (
                          <Suspense fallback={null}>
                            <UnreadMessageBadge />
                          </Suspense>
                        )}
                      </div>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="pb-20">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <LanguageToggle />
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="w-full justify-start"
              >
                <LogOut className="h-4 w-4" />
                {!isCollapsed && <span>{t("nav.signout")}</span>}
              </Button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
